/**
 * DataDictionaryReport Component
 *
 * Professional data dictionary showing all COBOL data structures.
 * Features comprehensive field catalog with search, filter, and export.
 *
 * Features:
 * - Searchable data field catalog
 * - Filter by data type, level, usage
 * - Sort by any column
 * - Export to CSV
 * - Group/hierarchy visualization
 * - Professional styling
 */

import { useState, useMemo } from 'react'
import {
  Database, Search, Download, Filter, ChevronUp, ChevronDown,
  FileText, Code2, Hash, Type, X, Layers
} from 'lucide-react'
import './DataDictionaryReport.css'

/**
 * Export to CSV
 */
function exportToCSV(data, filename = 'data-dictionary.csv') {
  const headers = ['Field Name', 'Level', 'PIC', 'Type', 'Size', 'Value', 'Java Type', 'Java Name', 'Line']
  const rows = data.map(item => [
    item.name,
    item.level,
    item.pic || '',
    item.isGroup ? 'Group' : 'Field',
    item.size || '',
    item.value || '',
    item.javaType || '',
    item.javaName || '',
    item.lineNum,
  ])

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
  ].join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  link.click()
}

/**
 * Get type badge color
 */
function getTypeBadge(javaType) {
  if (!javaType) return { color: '#565f89', label: 'Unknown' }
  if (javaType.includes('String')) return { color: '#7aa2f7', label: 'String' }
  if (javaType.includes('BigDecimal') || javaType.includes('Integer')) return { color: '#9ece6a', label: 'Numeric' }
  if (javaType.includes('Date')) return { color: '#e0af68', label: 'Date' }
  if (javaType.includes('Boolean')) return { color: '#bb9af7', label: 'Boolean' }
  return { color: '#7dcfff', label: javaType }
}

/**
 * Main DataDictionaryReport component
 */
function DataDictionaryReport({ dataModel, title = 'Data Dictionary' }) {
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState('lineNum')
  const [sortDirection, setSortDirection] = useState('asc')
  const [filterType, setFilterType] = useState('all')
  const [filterLevel, setFilterLevel] = useState('all')
  const [showFilters, setShowFilters] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 50

  // Parse data model
  const dictionaryData = useMemo(() => {
    if (!dataModel?.fields) return []

    return dataModel.fields.map(field => ({
      name: field.name || '',
      level: field.level || '',
      lineNum: field.line_num || 0,
      pic: field.pic || '',
      javaType: field.java_type || '',
      javaName: field.java_name || '',
      size: field.size || 0,
      decimalPlaces: field.decimal_places || 0,
      value: field.value || '',
      comp: field.comp || '',
      occurs: field.occurs || '',
      redefines: field.redefines || '',
      isFiller: field.is_filler || false,
      isGroup: field.is_group || false,
      rawText: field.raw_text || '',
    }))
  }, [dataModel])

  // Filter and sort
  const filteredAndSortedData = useMemo(() => {
    let result = [...dictionaryData]

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      result = result.filter(item =>
        item.name.toLowerCase().includes(term) ||
        item.javaName.toLowerCase().includes(term) ||
        item.pic.toLowerCase().includes(term)
      )
    }

    // Type filter
    if (filterType !== 'all') {
      if (filterType === 'group') {
        result = result.filter(item => item.isGroup)
      } else if (filterType === 'field') {
        result = result.filter(item => !item.isGroup)
      } else if (filterType === 'string') {
        result = result.filter(item => item.javaType?.includes('String'))
      } else if (filterType === 'numeric') {
        result = result.filter(item => item.javaType?.includes('BigDecimal') || item.javaType?.includes('Integer'))
      }
    }

    // Level filter
    if (filterLevel !== 'all') {
      result = result.filter(item => item.level === filterLevel)
    }

    // Sort
    result.sort((a, b) => {
      let aVal = a[sortField]
      let bVal = b[sortField]

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase()
        bVal = bVal.toLowerCase()
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1
      } else {
        return aVal < bVal ? 1 : -1
      }
    })

    return result
  }, [dictionaryData, searchTerm, sortField, sortDirection, filterType, filterLevel])

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedData.length / itemsPerPage)
  const paginatedData = filteredAndSortedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Get unique levels
  const uniqueLevels = useMemo(() => {
    const levels = new Set(dictionaryData.map(item => item.level))
    return Array.from(levels).sort((a, b) => parseInt(a) - parseInt(b))
  }, [dictionaryData])

  // Summary stats
  const totalFields = dictionaryData.length
  const groupItems = dictionaryData.filter(i => i.isGroup).length
  const stringFields = dictionaryData.filter(i => i.javaType?.includes('String')).length
  const numericFields = dictionaryData.filter(i => i.javaType?.includes('BigDecimal') || i.javaType?.includes('Integer')).length

  if (dictionaryData.length === 0) {
    return (
      <div className="data-dictionary-empty">
        <Database size={48} strokeWidth={1.5} />
        <p>No data dictionary available</p>
      </div>
    )
  }

  return (
    <div className="data-dictionary-report">
      {/* Header */}
      <div className="dictionary-header">
        <div className="dictionary-title">
          <h2><Database size={24} /> {title}</h2>
          <p className="dictionary-subtitle">{totalFields} data fields defined</p>
        </div>
        <div className="dictionary-actions">
          <button
            onClick={() => exportToCSV(filteredAndSortedData)}
            className="export-btn"
            title="Export to CSV"
          >
            <Download size={16} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="dictionary-summary">
        <div className="summary-card">
          <FileText size={20} />
          <div className="summary-content">
            <div className="summary-value">{totalFields}</div>
            <div className="summary-label">Total Fields</div>
          </div>
        </div>
        <div className="summary-card">
          <Layers size={20} color="#bb9af7" />
          <div className="summary-content">
            <div className="summary-value">{groupItems}</div>
            <div className="summary-label">Group Items</div>
          </div>
        </div>
        <div className="summary-card">
          <Type size={20} color="#7aa2f7" />
          <div className="summary-content">
            <div className="summary-value">{stringFields}</div>
            <div className="summary-label">String Fields</div>
          </div>
        </div>
        <div className="summary-card">
          <Hash size={20} color="#9ece6a" />
          <div className="summary-content">
            <div className="summary-value">{numericFields}</div>
            <div className="summary-label">Numeric Fields</div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="dictionary-controls">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search fields..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value)
              setCurrentPage(1)
            }}
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="clear-search">
              <X size={16} />
            </button>
          )}
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`filter-btn ${showFilters ? 'active' : ''}`}
        >
          <Filter size={16} />
          Filters
        </button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filter-group">
            <label>Data Type</label>
            <div className="filter-options">
              <button
                className={`filter-chip ${filterType === 'all' ? 'active' : ''}`}
                onClick={() => { setFilterType('all'); setCurrentPage(1) }}
              >
                All
              </button>
              <button
                className={`filter-chip ${filterType === 'group' ? 'active' : ''}`}
                onClick={() => { setFilterType('group'); setCurrentPage(1) }}
              >
                <Layers size={14} /> Groups
              </button>
              <button
                className={`filter-chip ${filterType === 'field' ? 'active' : ''}`}
                onClick={() => { setFilterType('field'); setCurrentPage(1) }}
              >
                <Code2 size={14} /> Fields
              </button>
              <button
                className={`filter-chip ${filterType === 'string' ? 'active' : ''}`}
                onClick={() => { setFilterType('string'); setCurrentPage(1) }}
              >
                <Type size={14} /> String
              </button>
              <button
                className={`filter-chip ${filterType === 'numeric' ? 'active' : ''}`}
                onClick={() => { setFilterType('numeric'); setCurrentPage(1) }}
              >
                <Hash size={14} /> Numeric
              </button>
            </div>
          </div>

          <div className="filter-group">
            <label>Level</label>
            <div className="filter-options">
              <button
                className={`filter-chip ${filterLevel === 'all' ? 'active' : ''}`}
                onClick={() => { setFilterLevel('all'); setCurrentPage(1) }}
              >
                All Levels
              </button>
              {uniqueLevels.slice(0, 6).map(level => (
                <button
                  key={level}
                  className={`filter-chip ${filterLevel === level ? 'active' : ''}`}
                  onClick={() => { setFilterLevel(level); setCurrentPage(1) }}
                >
                  Level {level}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="dictionary-table-container">
        <table className="dictionary-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('lineNum')} className="sortable numeric">
                Line
                {sortField === 'lineNum' && (sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
              </th>
              <th onClick={() => handleSort('level')} className="sortable">
                Level
                {sortField === 'level' && (sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
              </th>
              <th onClick={() => handleSort('name')} className="sortable">
                Field Name
                {sortField === 'name' && (sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
              </th>
              <th onClick={() => handleSort('pic')} className="sortable">
                PIC
                {sortField === 'pic' && (sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
              </th>
              <th onClick={() => handleSort('size')} className="sortable numeric">
                Size
                {sortField === 'size' && (sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
              </th>
              <th>Value</th>
              <th onClick={() => handleSort('javaType')} className="sortable">
                Type
                {sortField === 'javaType' && (sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
              </th>
              <th onClick={() => handleSort('javaName')} className="sortable">
                Java Name
                {sortField === 'javaName' && (sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((item, index) => {
              const { color, label } = getTypeBadge(item.javaType)
              const indent = Math.max(0, parseInt(item.level) - 1) * 12

              return (
                <tr key={index} className={item.isGroup ? 'group-item' : ''}>
                  <td className="numeric line-num">{item.lineNum}</td>
                  <td className="level-num">{item.level}</td>
                  <td className="field-name" style={{ paddingLeft: `${12 + indent}px` }}>
                    {item.isGroup && <Layers size={14} style={{ marginRight: '6px', color: '#bb9af7' }} />}
                    {item.name}
                  </td>
                  <td className="pic-clause">{item.pic}</td>
                  <td className="numeric">{item.size > 0 ? item.size : '-'}</td>
                  <td className="value-display">{item.value || '-'}</td>
                  <td>
                    <span className="type-badge" style={{ borderColor: color, color }}>
                      {label}
                    </span>
                  </td>
                  <td className="java-name">{item.javaName}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="dictionary-pagination">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span className="page-info">
            Page {currentPage} of {totalPages} ({filteredAndSortedData.length} fields)
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  )
}

export default DataDictionaryReport
